/* eslint-disable react-hooks/exhaustive-deps */
import {
  Badge,
  Box,
  Button,
  Flex,
  Image,
  Menu,
  Portal,
  Spinner,
  Stack,
  Switch,
  Table,
  Text
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { DotsThree, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

import { fetchAPI } from '@/api/fetch';
import {
  apiGetCandidates,
  apiDeleteCandidate,
  apiGetVeteransResumeCandidates,
  GetCandidatesParams,
  GetCandidatesResponse,
  GetCandidatesSortBy,
  GetFitPositionsResponse,
  apiGetFitPositions
} from '@/api/services/candidate.service';
import { apiGetAgencyCustomFeatures } from '@/api/services';
import { SortOrder } from '@/api/services/job_candidate.service';
import { Candidate } from '@/types/Candidate';
import { AgencyCustomFeature } from '@/types/AgencyCustomFeature';

import {
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot
} from '@/components/ui/pagination';
import { SearchBox } from '@/components/ui/searchbox';
import { Checkbox } from '@/components/ui/checkbox';
import { FunnelSimple } from '@phosphor-icons/react';

import emptyJobImage from '@/assets/image/empty-job-cover.svg';
import AddNewCandidate from './AddNewCandidate';
import { AgencyFeature, CandidateStatus, getCandidateStatusLabel } from '@/types/enums';
import { ConfirmationDialog } from '@/components/dialogs/BaseDialog';
import { getCandidateFullName } from '@/utils';

// Constants
const AUTO_RELOAD_INTERVAL = 5000;

interface TableHeaderProps {
  headers: { key: GetCandidatesSortBy | null; label: string; width: string; hasFilter?: boolean }[];
  sortBy: GetCandidatesSortBy;
  sortOrder: SortOrder;
  onSort: (field: GetCandidatesSortBy) => void;
  fitPositions: string[];
  selectedFitPositions: string[];
  onFitPositionsChange: (values: string[]) => void;
}

interface CandidateTableRowProps {
  candidate: Candidate;
  onNavigate: (candidateId: string) => void;
  onDelete?: (candidateId: string) => void;
}

interface FitPositionsCellProps {
  fitPositions: string[];
  isExtracting: boolean;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  headers,
  sortBy,
  sortOrder,
  onSort,
  fitPositions,
  selectedFitPositions,
  onFitPositionsChange
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilter, setTempFilter] = useState<string[]>(selectedFitPositions);

  useEffect(() => {
    setTempFilter(selectedFitPositions);
  }, [selectedFitPositions]);

  const getSortIcon = (field: GetCandidatesSortBy | null) => {
    if (field === null || sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ArrowUp size={16} weight="bold" />
    ) : (
      <ArrowDown size={16} weight="bold" />
    );
  };

  const handleSortClick = (field: GetCandidatesSortBy | null) => {
    if (field !== null) {
      onSort(field);
    }
  };

  const handleFilterOpen = useCallback(() => {
    if (selectedFitPositions.length === 0) {
      setTempFilter(fitPositions);
    } else {
      setTempFilter(selectedFitPositions);
    }
    setIsFilterOpen(true);
  }, [selectedFitPositions, fitPositions]);

  const handlePositionToggle = useCallback((position: string) => {
    setTempFilter((prev) => {
      const newFilter = prev.includes(position)
        ? prev.filter((p) => p !== position)
        : [...prev, position];
      return newFilter;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setTempFilter((prev) => {
      if (prev.length === fitPositions.length) {
        return [];
      } else {
        return [...fitPositions];
      }
    });
  }, [fitPositions]);

  const handleApply = useCallback(() => {
    onFitPositionsChange(tempFilter);
    setIsFilterOpen(false);
  }, [tempFilter, onFitPositionsChange]);

  const handleCancel = useCallback(() => {
    setTempFilter(selectedFitPositions);
    setIsFilterOpen(false);
  }, [selectedFitPositions]);

  return (
    <Table.Row bg="neutrals.50" h="48px">
      {headers.map((header, index) => (
        <Table.ColumnHeader
          key={header.key || `empty-${index}`}
          fontSize="14px"
          fontWeight="bold"
          borderColor="neutrals.300"
          pl={index === 0 ? '32px' : '16px'}
          width={header.width}
          cursor={header.key === null ? 'default' : 'pointer'}
          onClick={() => handleSortClick(header.key)}
          _hover={header.key !== null ? { bg: 'neutrals.100' } : {}}
        >
          <Flex align="center" gap="8px">
            {header.label}
            {getSortIcon(header.key) && (
              <Text fontSize="12px" color="primary.900">
                {getSortIcon(header.key)}
              </Text>
            )}
            {header.hasFilter && (
              <Menu.Root
                open={isFilterOpen}
                onOpenChange={(details) => {
                  if (details.open) {
                    handleFilterOpen();
                  } else {
                    setIsFilterOpen(false);
                  }
                }}
              >
                <Menu.Trigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    h="24px"
                    w="24px"
                    p="0"
                    borderRadius="full"
                    bg={
                      selectedFitPositions.length !== 0 &&
                      selectedFitPositions.length !== fitPositions.length
                        ? 'primary.100'
                        : 'transparent'
                    }
                    _hover={{ bg: 'neutrals.100' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterOpen();
                    }}
                    aria-label="Filter by categories"
                  >
                    <FunnelSimple size={12} weight="bold" />
                  </Button>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content minW="240px" p="16px">
                      <Flex direction="column" gap="12px">
                        <Text fontSize="14px" fontWeight="bold" color="neutrals.700">
                          Filter by Categories
                        </Text>
                        <Checkbox
                          size="sm"
                          checked={tempFilter.length === fitPositions.length}
                          onCheckedChange={handleSelectAll}
                          cursor="pointer"
                          colorPalette="blue"
                        >
                          Select All
                        </Checkbox>
                        <Box borderTop="1px solid" borderColor="neutrals.200" my="4px" />
                        {fitPositions.map((position) => (
                          <Checkbox
                            key={position}
                            size="sm"
                            checked={tempFilter.includes(position)}
                            onCheckedChange={() => handlePositionToggle(position)}
                            cursor="pointer"
                            colorPalette="blue"
                          >
                            {position}
                          </Checkbox>
                        ))}
                        <Box borderTop="1px solid" borderColor="neutrals.200" my="8px" />
                        <Flex gap="8px" justify="flex-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            borderRadius="8px"
                            onClick={handleCancel}
                            fontSize="14px"
                            h="32px"
                            px="12px"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleApply}
                            borderRadius="8px"
                            fontSize="14px"
                            h="32px"
                            px="12px"
                            bg="primary.900"
                            color="white"
                            _hover={{ bg: 'primary.800' }}
                          >
                            Apply
                          </Button>
                        </Flex>
                      </Flex>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            )}
          </Flex>
        </Table.ColumnHeader>
      ))}
      <Table.ColumnHeader
        fontSize="14px"
        fontWeight="bold"
        borderColor="neutrals.300"
        textAlign="end"
        width="100px"
      />
    </Table.Row>
  );
};

const FitPositionsCell: React.FC<FitPositionsCellProps> = ({ fitPositions, isExtracting }) => {
  if (isExtracting) {
    return (
      <Text fontSize="14px" color="neutrals.500">
        extracting...
      </Text>
    );
  }

  if (!fitPositions || fitPositions.length === 0) {
    return (
      <Text fontSize="14px" color="neutrals.400">
        No positions
      </Text>
    );
  }

  const visiblePositions = fitPositions.slice(0, 2);

  return (
    <Flex gap="6px" align="center" flexWrap="wrap">
      {visiblePositions.map((position, index) => (
        <Badge
          key={index}
          bg="blue.100"
          color="blue.800"
          px="8px"
          py="2px"
          borderRadius="full"
          fontSize="10px"
          fontWeight="medium"
        >
          {position}
        </Badge>
      ))}
    </Flex>
  );
};

const CandidateTableRow: React.FC<CandidateTableRowProps> = ({
  candidate,
  onNavigate,
  onDelete
}) => {
  const fatal = candidate.status === CandidateStatus.Fatal;
  const extracting = candidate.status === CandidateStatus.ResumeExtracting;
  const showActions = !extracting && candidate.allow_delete && onDelete;

  const handleNavigate = () => {
    if (!extracting && !fatal) {
      onNavigate(candidate.candidate_id);
    }
  };

  const tableRow = (
    <Table.Row
      key={candidate.candidate_id}
      _hover={{
        cursor: extracting || fatal ? 'disabled' : 'pointer',
        bg: 'neutrals.50'
      }}
      color={fatal ? 'error.500' : extracting ? 'neutrals.500' : 'neutrals.900'}
      bg={fatal ? 'error.100' : 'transparent'}
      h="42px"
    >
      <Table.Cell fontSize="14px" p="0" width="200px" onClick={handleNavigate}>
        <Box pl="32px">{candidate.unique_id}</Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" pl="16px" width="250px" onClick={handleNavigate}>
        <Box>
          {extracting
            ? 'extracting...'
            : getCandidateFullName(candidate.first_name, candidate.last_name) || 'N/A'}
        </Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" pl="16px" width="300px" onClick={handleNavigate}>
        <Box>{extracting ? 'extracting...' : candidate.email || 'N/A'}</Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" pl="16px" width="350px" onClick={handleNavigate}>
        <Box>
          <FitPositionsCell fitPositions={candidate.fit_positions} isExtracting={extracting} />
        </Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" pl="16px" width="180px" onClick={handleNavigate}>
        <Box>{new Date(candidate.created_at * 1000).toLocaleDateString()}</Box>
      </Table.Cell>
      <Table.Cell
        fontSize="16px"
        textAlign="end"
        py="4px"
        width="100px"
        onClick={showActions ? undefined : handleNavigate}
      >
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          {showActions && (
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button variant="ghost" borderRadius="full">
                  <DotsThree size="24px" />
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    {onDelete && (
                      <Menu.Item
                        value="delete"
                        onClick={() => onDelete(candidate.candidate_id)}
                        _hover={{ cursor: 'pointer', bg: 'error.50' }}
                        fontSize="14px"
                        p="8px 12px"
                        textAlign="left"
                        color="error.500"
                      >
                        Delete
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          )}
        </Box>
      </Table.Cell>
    </Table.Row>
  );

  if (fatal) {
    return (
      <Tooltip
        content={`Failed to extract data from resume "${candidate.original_resume_file_name}". Please verify the file contains valid candidate information and work experience.`}
        openDelay={0}
        closeDelay={0}
        contentProps={{
          css: {
            fontSize: '14px'
          }
        }}
        portalled
      >
        {tableRow}
      </Tooltip>
    );
  }

  return tableRow;
};

export default function CandidateIndex() {
  const pageSize = 10;
  const [isLoading, setIsLoading] = useState(true);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [fitPositions, setFitPositions] = useState<string[]>([]);
  const [selectedFitPositions, setSelectedFitPositions] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<GetCandidatesSortBy>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [fetchDataError, setFetchDataError] = useState(false);
  const [autoReload, setAutoReload] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVeteransMode, setIsVeteransMode] = useState(false);
  const [hasVeteransResumeFeature, setHasVeteransResumeFeature] = useState(false);

  const navigate = useNavigate();

  const loadCandidates = async (
    options: {
      params?: Partial<GetCandidatesParams>;
      showLoading?: boolean;
    } = {}
  ) => {
    const { params = {}, showLoading = false } = options;

    if (showLoading) {
      setIsLoading(true);
    }
    setFetchDataError(false);

    const requestParams: GetCandidatesParams = {
      page: currentPage,
      limit: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
      keyword: keyword,
      fit_positions: selectedFitPositions.length > 0 ? selectedFitPositions : undefined,
      ...params
    };

    try {
      const apiCall = isVeteransMode ? apiGetVeteransResumeCandidates : apiGetCandidates;
      const response = await fetchAPI<GetCandidatesResponse>({
        request: () => apiCall({ params: requestParams })
      });

      if (response.success) {
        const { candidates, current_page, total_pages } = response.data;
        const hasExtractingCandidates = candidates.some(
          (candidate) => candidate.status === CandidateStatus.ResumeExtracting
        );

        setCandidates(candidates);
        setCurrentPage(current_page);
        setTotalPages(total_pages);
        setAutoReload(hasExtractingCandidates);
      } else {
        console.error('Failed to fetch candidates:', response.message);
        setFetchDataError(true);
      }
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
      setFetchDataError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCandidates({
      showLoading: true
    });
  }, [sortBy, sortOrder, selectedFitPositions, isVeteransMode]);

  useEffect(() => {
    const fetchFitPositions = async () => {
      if (fitPositions.length === 0) {
        try {
          const response = await fetchAPI<GetFitPositionsResponse>({
            request: () => apiGetFitPositions()
          });

          if (response.success) {
            setFitPositions(response.data.fit_positions);
          } else {
            console.error('Failed to fetch fit positions:', response.message);
          }
        } catch (error) {
          console.error('Failed to fetch fit positions:', error);
        }
      }
    };

    fetchFitPositions();
  }, []);

  useEffect(() => {
    fetchAPI<AgencyCustomFeature[]>({ request: apiGetAgencyCustomFeatures }).then((res) => {
      if (res.success) {
        setHasVeteransResumeFeature(
          res.data.some((f) => f.feature_key === AgencyFeature.VeteransResume && f.is_enabled)
        );
      }
    });
  }, []);

  // Auto-reload when there are candidates with ResumeExtracting status
  useEffect(() => {
    if (!autoReload) return;

    const intervalId = setInterval(() => {
      loadCandidates({
        params: {
          page: currentPage,
          keyword: keyword,
          fit_positions: selectedFitPositions.length > 0 ? selectedFitPositions : undefined
        }
      });
    }, AUTO_RELOAD_INTERVAL);

    return () => clearInterval(intervalId);
  }, [autoReload, currentPage, keyword, selectedFitPositions, sortBy, sortOrder, loadCandidates]);

  const searchCandidates = useCallback(
    debounce(async (searchKeyword: string) => {
      await setKeyword(searchKeyword);
      await setCurrentPage(1);
      await loadCandidates({
        showLoading: true,
        params: {
          page: 1,
          keyword: searchKeyword,
          fit_positions: selectedFitPositions.length > 0 ? selectedFitPositions : undefined
        }
      });
    }, 500),
    [selectedFitPositions, sortBy, sortOrder]
  );

  const changePage = async (page: number) => {
    setCurrentPage(page);
    await loadCandidates({ showLoading: true, params: { page } });
  };

  const handleNavigateToCandidate = useCallback(
    (candidateId?: string) => {
      if (candidateId) navigate(`/candidate/${candidateId}`);
    },
    [navigate]
  );

  const handleAddCandidate = async () => {
    loadCandidates();
  };

  const handleDeleteCandidate = useCallback((candidateId: string) => {
    setCandidateToDelete(candidateId);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteCandidate = async () => {
    if (!candidateToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetchAPI({
        request: () => apiDeleteCandidate({ candidateId: candidateToDelete })
      });

      if (response.success) {
        await loadCandidates({ showLoading: true });
        setDeleteDialogOpen(false);
        setCandidateToDelete(null);
      } else {
        console.error('Failed to delete candidate:', response.message);
      }
    } catch (error) {
      console.error('Failed to delete candidate:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteCandidate = () => {
    setDeleteDialogOpen(false);
    setCandidateToDelete(null);
    setIsDeleting(false);
  };

  const handleSort = useCallback(
    (field: GetCandidatesSortBy) => {
      const defaultOrder = field === 'name' ? 'asc' : 'desc';

      if (sortBy === field) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(field);
        setSortOrder(defaultOrder);
      }
      setCurrentPage(1);
    },
    [sortBy, sortOrder]
  );

  const handleFitPositionsChange = useCallback((values: string[]) => {
    setSelectedFitPositions(values);
    setCurrentPage(1);
  }, []);

  const handleExportCSV = async () => {
    try {
      const response = await fetchAPI<GetCandidatesResponse>({
        request: () =>
          apiGetVeteransResumeCandidates({
            params: {
              page: 1,
              limit: 10000,
              sort_by: sortBy,
              sort_order: sortOrder,
              keyword: keyword,
              fit_positions: selectedFitPositions.length > 0 ? selectedFitPositions : undefined
            }
          })
      });

      if (!response.success) return;

      const header = ['Candidate ID', 'Name', 'Email', 'Phone', 'Status', 'Created Date'];
      const rows = response.data.candidates.map((c) => [
        c.unique_id,
        getCandidateFullName(c.first_name, c.last_name) || 'N/A',
        c.email || 'N/A',
        c.phone || 'N/A',
        getCandidateStatusLabel(c.status),
        new Date(c.created_at * 1000).toLocaleString(undefined, { timeZoneName: 'short' })
      ]);

      const csvContent = [header, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'veterans_resume_candidates.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const headers = [
    {
      key: 'unique_id' as GetCandidatesSortBy,
      label: 'Candidate ID',
      width: '200px'
    },
    { key: 'name' as GetCandidatesSortBy, label: 'Name', width: '250px' },
    { key: null, label: 'Email', width: '300px' },
    { key: null, label: 'Categories', width: '350px', hasFilter: true },
    {
      key: 'created_at' as GetCandidatesSortBy,
      label: 'Created Date',
      width: '180px'
    }
  ];

  return (
    <Flex w="100%" maxW="calc(100vw - 90px)" flexDir="column" p="28px 40px">
      <Flex justify="space-between" align="center" mb="24px">
        <Text fontSize="32px" color="neutrals.7900">
          {isVeteransMode ? 'Candidates from Resume Generator' : 'Candidates'}
        </Text>
        {hasVeteransResumeFeature && (
          <Flex direction="column" align="end" gap="8px">
            <Text fontSize="14px" color="neutrals.700">
              Military Resume Generator
            </Text>
            <Switch.Root
              checked={isVeteransMode}
              onCheckedChange={({ checked }) => {
                setIsVeteransMode(checked);
                setCurrentPage(1);
              }}
              colorPalette="blue"
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
          </Flex>
        )}
      </Flex>

      <Box
        bg="background.secondary"
        border="1px solid"
        borderColor="neutrals.300"
        borderRadius="8px"
        flex="1"
        boxShadow="sm"
        _hover={{ boxShadow: 'md' }}
        transition="all 0.2s ease-in-out"
        p="32px"
      >
        <Flex direction="column" justify="flex-start" align="flex-start" h="100%">
          <Flex justifyContent="space-between" alignItems="center" w="100%" mb="24px">
            <SearchBox placeholder="Search candidate name or email" search={searchCandidates} />
            {isVeteransMode ? (
              <Button
                borderRadius="8px"
                fontSize="14px"
                h="40px"
                px="16px"
                bg="primary.900"
                color="white"
                _hover={{ bg: 'primary.800' }}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            ) : (
              <AddNewCandidate onAddCandidate={handleAddCandidate} />
            )}
          </Flex>

          {fetchDataError ? (
            <Flex direction="column" justify="center" align="center" height="400px" w="100%">
              <Text fontSize="20px" fontWeight="bold" color="error.700" textAlign="center" mb="8px">
                Unable to load candidates.
              </Text>
              <Text fontSize="16px" color="error.700" textAlign="center">
                Please try again later or contact support for assistance.
              </Text>
            </Flex>
          ) : (
            <>
              <Box overflowX="auto" w="100%" maxW="100vw">
                <Table.Root variant="line" minW="1380px">
                  <Table.Header>
                    <TableHeader
                      headers={headers}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      fitPositions={fitPositions}
                      selectedFitPositions={selectedFitPositions}
                      onFitPositionsChange={handleFitPositionsChange}
                    />
                  </Table.Header>
                  <Table.Body>
                    {!isLoading &&
                      candidates.map((candidate) => (
                        <CandidateTableRow
                          key={candidate.candidate_id}
                          candidate={candidate}
                          onNavigate={handleNavigateToCandidate}
                          onDelete={handleDeleteCandidate}
                        />
                      ))}
                  </Table.Body>
                </Table.Root>
              </Box>

              {!isLoading && candidates.length > 0 && (
                <Flex justifyContent="flex-end" alignItems="center" mt="16px">
                  <PaginationRoot
                    count={totalPages * pageSize}
                    pageSize={pageSize}
                    page={currentPage}
                    onPageChange={(details) => changePage(details.page)}
                  >
                    <Stack wrap="wrap" direction="row">
                      <PaginationPrevTrigger />
                      <Text fontSize="14px" color="neutrals.700" alignSelf="center" mx="8px">
                        {currentPage} / {totalPages}
                      </Text>
                      <PaginationNextTrigger />
                    </Stack>
                  </PaginationRoot>
                </Flex>
              )}

              {isLoading ? (
                <Flex
                  flexDir="column"
                  alignItems="center"
                  justifyContent="center"
                  py="120px"
                  w="100%"
                  h="400px"
                >
                  <Spinner boxSize="32px" borderWidth="4px" color="primary.700" />
                </Flex>
              ) : (
                candidates.length === 0 && (
                  <Flex
                    flexDir="column"
                    alignItems="center"
                    justifyContent="center"
                    py="80px"
                    w="100%"
                    h="400px"
                  >
                    <Image src={emptyJobImage} h="280px" w="280px" mt="64px" />
                    <Text fontSize="20px" fontWeight="bold" color="neutrals.700" mb="8px">
                      No Candidates Found
                    </Text>
                    <Text fontSize="16px" color="neutrals.500" mb="16px">
                      Start by adding your first candidate.
                    </Text>
                    <AddNewCandidate onAddCandidate={handleAddCandidate} filled />
                  </Flex>
                )
              )}
            </>
          )}
        </Flex>
      </Box>

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={cancelDeleteCandidate}
        title="Delete Candidate"
        message="This candidate has failed resume processing. Are you sure you want to permanently delete this candidate? This action cannot be undone."
        onConfirm={confirmDeleteCandidate}
        confirmLabel="Delete"
        isDangerous={true}
        isConfirmLoading={isDeleting}
      />
    </Flex>
  );
}
